import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"
import { French } from "flatpickr/dist/l10n/fr.js"

export default class extends Controller {
  static targets = ["display", "startDate", "endDate", "panel", "calendar"]
  static values = { locale: String }

  connect() {
    this.boundClickOutside = this.closeOnClickOutside.bind(this)
    this.boundEscape = this.closeOnEscape.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    document.addEventListener("keydown", this.boundEscape)

    const initialStart = this.startDateTarget.value
    const initialEnd = this.endDateTarget.value
    const hasInitialRange = initialStart && initialEnd

    this.flatpickr = flatpickr(this.calendarTarget, {
      mode: "range",
      inline: true,
      dateFormat: "Y-m-d",
      minDate: "today",
      locale: this.localeValue === "fr" ? French : undefined,
      defaultDate: hasInitialRange ? [initialStart, initialEnd] : undefined,
      onChange: (selectedDates) => this.syncHiddenInputs(selectedDates),
    })

    if (hasInitialRange) {
      this.displayTarget.value = this.#formatDisplay([new Date(initialStart), new Date(initialEnd)])
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    document.removeEventListener("keydown", this.boundEscape)
    if (this.flatpickr) {
      this.flatpickr.destroy()
      this.flatpickr = null
    }
  }

  open() {
    this.panelTarget.classList.remove("hidden")
  }

  close() {
    this.panelTarget.classList.add("hidden")
  }

  confirm() {
    const dates = this.flatpickr?.selectedDates ?? []
    this.displayTarget.value = this.#formatDisplay(dates)
    this.close()
  }

  cancel() {
    this.flatpickr?.clear()
    this.displayTarget.value = ""
    this.startDateTarget.value = ""
    this.endDateTarget.value = ""
    this.close()
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  closeOnEscape(event) {
    if (event.key === "Escape") this.close()
  }

  syncHiddenInputs(selectedDates) {
    const [start, end] = selectedDates
    this.startDateTarget.value = start ? this.#formatISO(start) : ""
    this.endDateTarget.value = end ? this.#formatISO(end) : ""
  }

  #formatDisplay(dates) {
    if (dates.length === 0) return ""
    const locale = this.localeValue || "fr"
    const opts = { day: "numeric", month: "short", year: "numeric" }
    if (dates.length === 1) return dates[0].toLocaleDateString(locale, opts)
    return `${dates[0].toLocaleDateString(locale, opts)} - ${dates[1].toLocaleDateString(locale, opts)}`
  }

  #formatISO(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }
}
